import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Printer, X } from 'lucide-react';
import { formatRial } from '@/lib/formatMoney';
import { getExpenseTypes } from '@/lib/mock/expenses';
import PrintReportShell from '@/components/print/PrintReportShell';
import {
  type ProjectExpensePrintColKey,
  releaseProjectExpenseCartexPrintMemory,
  takeProjectExpenseCartexPrintPayloadForPage,
} from '@/lib/projectExpenseCartexPrint';

const ProjectExpenseCartexPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [payload] = React.useState(() => takeProjectExpenseCartexPrintPayloadForPage());
  const [types] = React.useState(() => getExpenseTypes());
  const typeLabel = React.useCallback(
    (id: string) => types.find((x) => x.id === id)?.title ?? id,
    [types],
  );
  const activeCols = (Object.entries(payload.printOptions?.tableColumns ?? {}) as Array<[ProjectExpensePrintColKey, boolean]>)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);
  const colLabel: Record<ProjectExpensePrintColKey, string> = {
    row: 'ردیف',
    date: 'تاریخ',
    beneficiary: 'طرف هزینه',
    type: 'نوع هزینه',
    payment: 'روش پرداخت',
    amount: 'مبلغ',
    sourceBank: 'بانک مبدا',
    destinationBank: 'بانک مقصد',
    attachment: 'پیوست',
    description: 'شرح',
  };


  React.useEffect(() => {
    return () => releaseProjectExpenseCartexPrintMemory();
  }, []);

  if (!payload || String(payload.project.id) !== String(id)) {
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <div className="mx-auto max-w-md rounded-xl border bg-white p-6 text-center">
          <p className="mb-4 text-sm text-slate-700">داده چاپ یافت نشد. لطفا از صفحه پروژه دوباره چاپ را بزنید.</p>
          <Link to={id ? `/projects/${id}` : '/projects'} className="text-sky-600 hover:underline">
            بازگشت
          </Link>
        </div>
      </div>
    );
  }

  const toolsPanel = (
    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-slate-700">
        <div className="font-bold">{payload.title}</div>
        <div>تولید گزارش: {payload.generatedAt}</div>
      </div>
      <div className="flex gap-2">
        <Link
          to={`/projects/${payload.project.id}`}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
        >
          <X className="h-4 w-4" />
          بستن
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700"
        >
          <Printer className="h-4 w-4" />
          چاپ / PDF
        </button>
      </div>
    </div>
  );

  return (
    <PrintReportShell pageSize="A4 landscape" toolsPanel={toolsPanel}>
      <div className="mb-3 rounded-md border-2 border-black p-2 text-center">
        <div className="border-b border-black pb-1 text-sm font-bold">{payload.title}</div>
        <div className="mt-1 text-xs text-slate-700">
          پروژه: {payload.project.label} | تعداد ردیف: {payload.summary.count} | جمع هزینه: {formatRial(payload.summary.totalAmount)}
        </div>
        <div className="mt-1 text-xs text-slate-600">
          فیلترها: شخص {payload.filters.beneficiaryLabel || 'همه'} | نوع {payload.filters.typeLabel || 'همه'} | تاریخ{' '}
          {payload.filters.dateFrom || '—'} تا {payload.filters.dateTo || '—'}
        </div>
      </div>

      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-200">
            {activeCols.map((c) => (
              <th key={c} className="border border-slate-500 p-1">
                {colLabel[c]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {payload.rows.map((r, i) => (
            <tr key={r.id}>
              {activeCols.map((c) => {
                if (c === 'row') return <td key={c} className="border border-slate-400 p-1 text-center">{i + 1}</td>;
                if (c === 'date') return <td key={c} className="border border-slate-400 p-1 text-center">{r.dateFa || '-'}</td>;
                if (c === 'beneficiary') return <td key={c} className="border border-slate-400 p-1">{r.beneficiary || '-'}</td>;
                if (c === 'type') return <td key={c} className="border border-slate-400 p-1">{r.expenseTypeTitle || typeLabel(r.expenseTypeId)}</td>;
                if (c === 'payment') return <td key={c} className="border border-slate-400 p-1 text-center">{r.paymentMethod}</td>;
                if (c === 'amount') {
                  return (
                    <td key={c} className="border border-slate-400 p-1 text-left font-mono" dir="ltr">
                      {formatRial(r.amount)}
                    </td>
                  );
                }
                if (c === 'sourceBank') return <td key={c} className="border border-slate-400 p-1">{r.sourceBankLabel || '-'}</td>;
                if (c === 'destinationBank') return <td key={c} className="border border-slate-400 p-1">{r.destinationBankLabel || '-'}</td>;
                if (c === 'attachment') return <td key={c} className="border border-slate-400 p-1">{r.attachmentName || '-'}</td>;
                return <td key={c} className="border border-slate-400 p-1">{r.description || '-'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </PrintReportShell>
  );
};

export default ProjectExpenseCartexPrintPage;
